import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext(null);

export const CCY_SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", NGN: "₦", GHS: "₵",
  KES: "KSh", ZAR: "R", JPY: "¥", CNY: "CN¥",
};

const REGION_TO_CCY = {
  US: "USD", GB: "GBP", NG: "NGN", GH: "GHS", KE: "KES", ZA: "ZAR",
  JP: "JPY", CN: "CNY", DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR",
  NL: "EUR", IE: "EUR", PT: "EUR", AT: "EUR", BE: "EUR",
};

function detectCurrency() {
  try {
    const stored = localStorage.getItem("goe-ccy");
    if (stored) return stored;
    const region = new Intl.Locale(navigator.language).region;
    return REGION_TO_CCY[region] || "USD";
  } catch {
    return "USD";
  }
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(detectCurrency);
  const [rates, setRates] = useState({ USD: 1 });
  const [ready, setReady] = useState(false);
  const [supported, setSupported] = useState(Object.keys(CCY_SYMBOLS));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const cached = localStorage.getItem("goe-rates");
      if (cached) {
        try {
          const { rates: r, day } = JSON.parse(cached);
          if (day === new Date().toISOString().slice(0, 10) && r) {
            setRates(r);
            setReady(true);
          }
        } catch {}
      }
      try {
        const { data } = await api.get("/rates");
        if (cancelled) return;
        setRates(data.rates);
        if (data.supported) setSupported(data.supported);
        setReady(true);
        localStorage.setItem(
          "goe-rates",
          JSON.stringify({ rates: data.rates, day: new Date().toISOString().slice(0, 10) })
        );
      } catch {
        setReady(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const setCurrency = (c) => {
    setCurrencyState(c);
    localStorage.setItem("goe-ccy", c);
  };

  const value = useMemo(() => {
    const convert = (amount, from = "USD", to = currency) => {
      const rf = rates[from] || 1;
      const rt = rates[to] || 1;
      const usd = amount / rf;
      return usd * rt;
    };
    const symbol = (c = currency) => CCY_SYMBOLS[c] || c + " ";
    const format = (amount, c = currency, opts = {}) => {
      const val = convert(amount, opts.from || "USD", c);
      const decimals = val >= 1000 ? 0 : val >= 1 ? 2 : 4;
      const num = val.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      const s = symbol(c);
      return `${s}${s.length > 1 ? " " : ""}${num}`;
    };
    return { currency, setCurrency, rates, ready, supported, convert, format, symbol };
  }, [currency, rates, ready, supported]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
